import {
  Chip,
  Divider,
  Popover,
  PopoverOrigin,
  PopoverProps,
  Stack,
  Typography,
} from "@mui/material";
import { FC, Fragment } from "react";
import { S3 } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";

interface LifecycleRulesCellRendererProps {
  data?: S3;
}

export const LifecycleRulesCellRenderer: FC<
  LifecycleRulesCellRendererProps
> = ({ data }) => {
  const { anchor, open, openMenu, closeMenu } = useMenuHook();
  const rules = data?.lifecycle_rule_names;

  if (!rules?.length) {
    return (
      <TypographyWithTooltip
        variant="body2"
        color={rules ? "text.secondary" : undefined}
        title={rules ? "Not configured" : "N/A"}
      />
    );
  }

  return (
    <Stack direction="row" alignItems="center" width="100%" overflow="hidden">
      <Chip
        size="small"
        onClick={rules.length > 1 ? openMenu : undefined}
        label={<TypographyWithTooltip variant="inherit" title={rules.at(0)} />}
        sx={typographyStyles}
        variant="outlined"
      />
      {rules.length > 1 && (
        <Chip
          label={`+${rules.length - 1}`}
          onClick={openMenu}
          variant="outlined"
          size="small"
          sx={{ ml: 0.5 }}
        />
      )}

      {open && (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={closeMenu}
          transformOrigin={transformOrigin}
          anchorOrigin={anchorOrigin}
          slotProps={slotProps}
        >
          <Stack spacing={0.5} py={1}>
            <Typography variant="body1" fontWeight="medium" px={2}>
              Lifecycle Rules ({rules.length})
            </Typography>
            <Divider />
            {rules.map((rule, index) => {
              return (
                <Fragment key={rule}>
                  <TypographyWithTooltip
                    variant="body2"
                    title={rule}
                    px={2}
                    py={0.5}
                    sx={{ flex: 1, cursor: "pointer", minWidth: 50 }}
                  />
                  {index !== rules.length - 1 && rules.length > 1 && (
                    <Divider />
                  )}
                </Fragment>
              );
            })}
          </Stack>
        </Popover>
      )}
    </Stack>
  );
};

const typographyStyles = {
  overflow: "hidden",
  width: "fit-content",
};

const slotProps: PopoverProps["slotProps"] = {
  paper: {
    sx: {
      minWidth: 200,
      maxWidth: 400,
    },
  },
};

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "left",
};

const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "left",
};
